import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import Plot from "react-plotly.js";
import "./PredictiveAnalysis.css";
import { apiRequest, logout, getAuthToken } from "../utils/api";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Regression Visualizations Component
function RegressionVisualizations({ actualValues, residuals, predictions, parsedRows, targetVariable }) {
  // Extract actual values from parsedRows if not provided by backend
  let actualVals = actualValues;
  let residualVals = residuals;
  
  if (actualVals.length === 0 && parsedRows.length > 0 && parsedRows.length === predictions.length) {
    // Extract actual values from parsedRows
    actualVals = parsedRows.map(row => {
      const val = row[targetVariable];
      return val !== undefined && val !== null && val !== '' ? parseFloat(val) : null;
    }).filter(v => v !== null && !isNaN(v));
    
    // Match length with predictions
    const minLen = Math.min(actualVals.length, predictions.length);
    actualVals = actualVals.slice(0, minLen);
    
    // Calculate residuals
    if (actualVals.length === predictions.length) {
      residualVals = actualVals.map((actual, idx) => actual - predictions[idx]);
    }
  }
  
  // Only show plots if we have actual values
  if (actualVals.length === 0 || actualVals.length !== predictions.length) {
    return (
      <div className="evaluation-metrics-section">
        <h2>Regression Analysis</h2>
        <div className="info-box">
          <p>Visualizations require actual target values in the test data. Please ensure your test CSV includes the target column "{targetVariable}" with valid numeric values.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="evaluation-metrics-section">
      <h2>Regression Analysis</h2>
      
      {/* Visualizations Grid */}
      <div className="regression-visuals-grid">
        {/* Actual vs Predicted Scatter Plot */}
        <div className="visual-card">
          <div className="visual-header">
            <h3>Actual vs Predicted</h3>
            <div className="visual-badge">Predictive Ability</div>
          </div>
          <div className="visual-explanation">
            <p>This plot shows how well the model predicts actual values. Points closer to the diagonal line (y=x) indicate better predictions. A perfect model would have all points on the diagonal line.</p>
          </div>
          <div className="plot-container-inline">
            <Plot
              data={[
                {
                  x: actualVals,
                  y: predictions,
                  type: 'scatter',
                  mode: 'markers',
                  name: 'Predictions',
                  marker: { color: '#1e3c72', size: 6, opacity: 0.6 }
                },
                {
                  x: actualVals.length > 0 ? [
                    Math.min(...actualVals),
                    Math.max(...actualVals)
                  ] : [0, 1],
                  y: actualVals.length > 0 ? [
                    Math.min(...actualVals),
                    Math.max(...actualVals)
                  ] : [0, 1],
                  type: 'scatter',
                  mode: 'lines',
                  name: 'Perfect Prediction (y=x)',
                  line: { color: '#dc3545', dash: 'dash', width: 2 }
                }
              ]}
              layout={{
                title: '',
                xaxis: { title: 'Actual Values' },
                yaxis: { title: 'Predicted Values' },
                autosize: true,
                showlegend: true,
                template: 'plotly_white',
                margin: { l: 60, r: 20, t: 20, b: 60 }
              }}
              config={{ responsive: true, displayModeBar: true }}
              style={{ width: '100%', height: '500px', minHeight: '500px' }}
            />
          </div>
        </div>

        {/* Residual Plot */}
        {residualVals.length > 0 && residualVals.length === predictions.length && (
          <div className="visual-card">
            <div className="visual-header">
              <h3>Residual Plot</h3>
              <div className="visual-badge">Error Analysis</div>
            </div>
            <div className="visual-explanation">
              <p>This plot shows the distribution of prediction errors (residuals). Ideally, residuals should be randomly scattered around zero with no patterns. Patterns indicate model limitations or data issues.</p>
            </div>
            <div className="plot-container-inline">
              <Plot
                data={[
                  {
                    x: predictions,
                    y: residualVals,
                    type: 'scatter',
                    mode: 'markers',
                    name: 'Residuals',
                    marker: { color: '#f57c00', size: 6, opacity: 0.6 }
                  },
                  {
                    x: predictions.length > 0 ? [
                      Math.min(...predictions),
                      Math.max(...predictions)
                    ] : [0, 1],
                    y: [0, 0],
                    type: 'scatter',
                    mode: 'lines',
                    name: 'Zero Residual Line',
                    line: { color: '#dc3545', dash: 'dash', width: 2 }
                  }
                ]}
                layout={{
                  title: '',
                  xaxis: { title: 'Predicted Values' },
                  yaxis: { title: 'Residuals (Actual - Predicted)' },
                  autosize: true,
                  showlegend: true,
                  template: 'plotly_white',
                  margin: { l: 60, r: 20, t: 20, b: 60 }
                }}
                config={{ responsive: true, displayModeBar: true }}
                style={{ width: '100%', height: '450px' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PredictiveAnalysis() {
  const [targetVariable, setTargetVariable] = useState("");
  const [testFile, setTestFile] = useState(null);
  const [testAccuracy, setTestAccuracy] = useState(null);
  const [uploadMessage, setUploadMessage] = useState("");
  const [modelUsed, setModelUsed] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [parsedRows, setParsedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState("");
  const [confusionMatrix, setConfusionMatrix] = useState(null);
  const [rocCurve, setRocCurve] = useState(null);
  const [isClassification, setIsClassification] = useState(false);
  // Regression-specific state
  const [r2Score, setR2Score] = useState(null);
  const [mae, setMae] = useState(null);
  const [rmse, setRmse] = useState(null);
  const [actualValues, setActualValues] = useState([]);
  const [residuals, setResiduals] = useState([]);
  const navigate = useNavigate();

  const [useSplit, setUseSplit] = useState(false);
  const [splitConfig, setSplitConfig] = useState(null);
  const [bestModel, setBestModel] = useState("");
  const [trainAccuracy, setTrainAccuracy] = useState(null);
  const [taskType, setTaskType] = useState("");
  const [datasetName, setDatasetName] = useState("");
  const [predReportLoading, setPredReportLoading] = useState(false);
  const [predReportError, setPredReportError] = useState("");

  useEffect(() => {
    const selectedTarget = localStorage.getItem("selectedTarget");
    setTargetVariable(selectedTarget || "");
    
    // Check if split was used
    const config = JSON.parse(localStorage.getItem("dataSplitConfig") || '{"use_100_percent": true}');
    const splitUsed = !config.use_100_percent;
    setUseSplit(splitUsed);
    setSplitConfig(config);

    // Load training metadata for KPI cards
    try {
      const cached = localStorage.getItem("cachedTrainingResults");
      if (cached) {
        const parsed = JSON.parse(cached);
        const results = Array.isArray(parsed.results) ? parsed.results : [];
        // Find best result by accuracy/r2
        const validResults = results.filter(r => !r.error);
        if (validResults.length > 0) {
          const best = validResults.reduce((a, b) => {
            const aScore = a.accuracy ?? a.r2_score ?? 0;
            const bScore = b.accuracy ?? b.r2_score ?? 0;
            return bScore > aScore ? b : a;
          });
          setBestModel(best.algorithm || "");
          setTrainAccuracy(best.accuracy ?? best.r2_score ?? null);
          const hasRegression = validResults.some(r => r.r2_score !== undefined);
          setTaskType(hasRegression ? "regression" : "classification");
        }
      }
    } catch (e) { /* ignore */ }

    // Dataset name
    const dsName = localStorage.getItem("uploadedDatasetName") || localStorage.getItem("selectedDatasetName") || "";
    setDatasetName(dsName);
    
    const interval = setInterval(() => {
      const currentTime = new Date();
      const hours = currentTime.getHours() % 12 || 12;
      const minutes = currentTime.getMinutes().toString().padStart(2, "0");
      const seconds = currentTime.getSeconds().toString().padStart(2, "0");
      const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
      setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setTestFile(file);
    setUploadMessage("");
    setTestAccuracy(null);
    setModelUsed("");
    setPredictions([]);
    setParsedRows([]);
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => setParsedRows(results.data),
    });
  };

  const handleRunPredictionWithSplit = async () => {
    // Use saved test set from split
    setLoading(true);
    setUploadMessage("Using saved test set from data split...");
    try {
      const form = new FormData();
      form.append("target_variable", targetVariable);
      form.append("use_saved_test_set", "true");
      const jobId = localStorage.getItem("trainingJobId");
      if (!jobId) {
        setUploadMessage("No training job found. Please run training first.");
        setLoading(false);
        return;
      }
      form.append("job_id", jobId);
      
      const json = await apiRequest("/api/predict", {
        method: "POST",
        body: form,
        contentType: "multipart/form-data",
      });
      if (json.status === "success") {
        setModelUsed(json.model_used);
        setPredictions(json.predictions);
        setTestAccuracy(json.accuracy);
        setIsClassification(json.is_classification || false);
        setConfusionMatrix(json.confusion_matrix || null);
        
        // Extract ROC curve data if available (classification only)
        if (json.confusion_matrix && json.confusion_matrix.roc_curve) {
          setRocCurve(json.confusion_matrix.roc_curve);
        } else {
          setRocCurve(null);
        }
        
        // Extract regression metrics if available
        if (json.r2_score !== undefined) setR2Score(json.r2_score);
        if (json.mae !== undefined) setMae(json.mae);
        if (json.rmse !== undefined) setRmse(json.rmse);
        if (json.actual_values) setActualValues(json.actual_values);
        if (json.residuals) setResiduals(json.residuals);
        
        // If test_data is provided (from saved test set), use it to populate parsedRows
        if (json.test_data && Array.isArray(json.test_data)) {
          setParsedRows(json.test_data);
        }
        
        setUploadMessage("Prediction completed successfully!");
      } else {
        setUploadMessage(json.message || "Prediction failed.");
      }
    } catch (error) {
      console.error("Prediction error:", error);
      setUploadMessage("Prediction failed: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleRunPrediction = async () => {
    if (!testFile) {
      setUploadMessage("Please upload a CSV file first.");
      return;
    }
    setLoading(true);
    const form = new FormData();
    form.append("file", testFile);
    form.append("target_variable", targetVariable);
    const jobId = localStorage.getItem("trainingJobId");
    if (!jobId) {
      setUploadMessage("No training job found. Please run training first.");
      setLoading(false);
      return;
    }
    form.append("job_id", jobId);
    try {
      const json = await apiRequest("/api/predict", {
        method: "POST",
        body: form,
        contentType: "multipart/form-data",
      });
      if (json.status === "success") {
        setModelUsed(json.model_used);
        setPredictions(json.predictions);
        setTestAccuracy(json.accuracy);
        setIsClassification(json.is_classification || false);
        setConfusionMatrix(json.confusion_matrix || null);
        
        // Extract ROC curve data if available (classification only)
        if (json.confusion_matrix && json.confusion_matrix.roc_curve) {
          setRocCurve(json.confusion_matrix.roc_curve);
        } else {
          setRocCurve(null);
        }
        
        // Extract regression metrics if available
        if (!json.is_classification) {
          setR2Score(json.r2_score || null);
          setMae(json.mae || null);
          setRmse(json.rmse || null);
          setActualValues(json.actual_values || []);
          setResiduals(json.residuals || []);
        } else {
          setR2Score(null);
          setMae(null);
          setRmse(null);
          setActualValues([]);
          setResiduals([]);
        }
        
        // If test_data is provided (from saved test set), use it to populate parsedRows
        if (json.test_data && Array.isArray(json.test_data)) {
          setParsedRows(json.test_data);
        }
        
        setUploadMessage("Prediction completed successfully.");
      } else {
        setUploadMessage("Error: " + json.message);
        setConfusionMatrix(null);
        setRocCurve(null);
        setR2Score(null);
        setMae(null);
        setRmse(null);
        setActualValues([]);
        setResiduals([]);
      }
    } catch (e) {
      console.error(e);
      setUploadMessage("Error: " + (e.message || "Server error during prediction."));
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePredictionReport = async () => {
    setPredReportLoading(true);
    setPredReportError("");
    try {
      // Include training results from localStorage for a comprehensive report
      const trainingData = JSON.parse(localStorage.getItem("trainingResults") || "{}");
      
      const payload = {
        target_variable: targetVariable,
        model_used: modelUsed,
        accuracy: testAccuracy,
        predictions: predictions,
        is_classification: isClassification,
        confusion_matrix: confusionMatrix,
        r2_score: r2Score,
        mae: mae,
        rmse: rmse,
        actual_values: actualValues,
        residuals: residuals,
        // Training data for comprehensive report
        training_results: trainingData.results || [],
        correlation_matrix: trainingData.correlation_matrix || {},
        feature_importance: trainingData.feature_importance || {},
      };
      const response = await fetch(`${API_BASE_URL}/api/report/generate-prediction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Report generation failed");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ML_Analysis_Report_${targetVariable}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Report generation error:", e);
      setPredReportError(e.message || "Failed to generate report");
    } finally {
      setPredReportLoading(false);
    }
  };

  return (
    <div className="predictive-analysis-page">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-content">
          <div className="nav-left-group">
            <div className="nav-logo" onClick={() => navigate("/")}>
              <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
            </div>
            <button className="back-nav-button" onClick={() => navigate("/results")}>
              Back
            </button>
          </div>
          <div className="nav-right">
            <div className="digital-clock">{time}</div>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Predictive Analysis</h1>
          <p className="header-subtitle">
            Upload test data to make predictions using the best trained model
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">

        {/* KPI Cards Row */}
        <div className="kpi-cards-row">
          <div className="kpi-card kpi-target">
            <div className="kpi-card-icon">{String.fromCodePoint(0x1F3AF)}</div>
            <div className="kpi-card-body">
              <div className="kpi-card-label">Target Variable</div>
              <div className="kpi-card-value">{targetVariable || "-"}</div>
            </div>
          </div>

          <div className="kpi-card kpi-model">
            <div className="kpi-card-icon">{String.fromCodePoint(0x1F916)}</div>
            <div className="kpi-card-body">
              <div className="kpi-card-label">Best Trained Model</div>
              <div className="kpi-card-value kpi-card-value--sm">{bestModel || "-"}</div>
            </div>
          </div>

          <div className="kpi-card kpi-accuracy">
            <div className="kpi-card-icon">{String.fromCodePoint(0x1F4C8)}</div>
            <div className="kpi-card-body">
              <div className="kpi-card-label">
                {taskType === "regression" ? "Best R\u00B2 Score" : "Best Train Accuracy"}
              </div>
              <div className="kpi-card-value kpi-card-value--green">
                {trainAccuracy !== null
                  ? taskType === "regression"
                    ? trainAccuracy.toFixed(4)
                    : `${(trainAccuracy * 100).toFixed(1)}%`
                  : "-"}
              </div>
            </div>
          </div>

          <div className="kpi-card kpi-split">
            <div className="kpi-card-icon">{String.fromCodePoint(0x1F500)}</div>
            <div className="kpi-card-body">
              <div className="kpi-card-label">Data Split</div>
              <div className="kpi-card-value kpi-card-value--sm">
                {useSplit
                  ? `${splitConfig?.train_ratio || 70}% Train / ${splitConfig?.test_ratio || 30}% Test`
                  : "100% Training"}
              </div>
            </div>
          </div>
        </div>

        {useSplit ? (
          <div className="predict-action-card">
            <div className="predict-action-card__left">
              <div className="predict-action-card__icon">{'\u26A1'}</div>
              <div>
                <h3 className="predict-action-card__title">Run Prediction with Saved Test Set</h3>
                <p className="predict-action-card__desc">
                  Your data was split {splitConfig?.train_ratio || 70}/{splitConfig?.test_ratio || 30} during training.
                  The held-out test set is ready &mdash; just launch the prediction.
                </p>
              </div>
            </div>
            <button
              onClick={handleRunPredictionWithSplit}
              disabled={loading}
              className="predict-launch-button"
            >
              {loading ? (
                <><span className="spinner"></span> Running...</>
              ) : (
                <><span>{'\u25B6'}</span> Launch Prediction</>
              )}
            </button>
          </div>
        ) : (
          <div className="predict-upload-card">
            <div className="predict-upload-card__header">
              <span className="predict-upload-card__icon">{String.fromCodePoint(0x1F4C2)}</span>
              <div>
                <h3 className="predict-action-card__title">Upload Test CSV</h3>
                <p className="predict-action-card__desc">
                  Provide a CSV with the same feature columns as training. Include the target column for evaluation.
                </p>
              </div>
            </div>

            <div className="predict-upload-card__guidelines">
              <span className="guideline-item">{'\u2713'} Same feature columns as training</span>
              <span className="guideline-item">{'\u2713'} Target column with ground-truth labels</span>
              <span className="guideline-item">{'\u2713'} Comma-delimited <code>.csv</code> format</span>
              <span className="guideline-item">{'\u2713'} Column names must match exactly</span>
            </div>

            <div className="predict-upload-card__actions">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="file-input"
                id="test-file-input"
              />
              <label htmlFor="test-file-input" className="file-choose-button">
                <span>{String.fromCodePoint(0x1F4C1)}</span>
                {testFile ? testFile.name : "Choose CSV File"}
              </label>
              <button
                onClick={handleRunPrediction}
                disabled={!testFile || loading}
                className="predict-launch-button"
              >
                {loading ? (
                  <><span className="spinner"></span> Running...</>
                ) : (
                  <><span>{'\u25B6'}</span> Run Prediction</>
                )}
              </button>
            </div>
          </div>
        )}

        {uploadMessage && (
          <div className={`message-box ${uploadMessage.includes("Error") ? "error" : "success"}`}>
            {uploadMessage}
          </div>
        )}

        {/* Accuracy/R² Score Display */}
        {testAccuracy !== null && testAccuracy !== undefined && !isNaN(testAccuracy) && (
          <div className="accuracy-box">
            <h3>{isClassification ? "Test Accuracy" : "R² Score (Accuracy)"}</h3>
            <p className="accuracy-value">
              {isClassification 
                ? `${(testAccuracy * 100).toFixed(2)}%`
                : testAccuracy.toFixed(4)
              }
            </p>
          </div>
        )}

        {/* Regression Metrics */}
        {!isClassification && testAccuracy !== null && testAccuracy !== undefined && (
          <div className="regression-metrics-section">
            <h2>Regression Metrics</h2>
            <div className="metrics-grid">
              <div className="metric-box">
                <div className="metric-label">R² Score</div>
                <div className="metric-value">
                  {r2Score !== null && r2Score !== undefined ? r2Score.toFixed(4) : testAccuracy.toFixed(4)}
                </div>
                <div className="metric-description">Coefficient of determination (closer to 1 is better)</div>
              </div>
              {(mae !== null && mae !== undefined) && (
                <div className="metric-box">
                  <div className="metric-label">MAE</div>
                  <div className="metric-value">{mae.toFixed(4)}</div>
                  <div className="metric-description">Mean Absolute Error (lower is better)</div>
                </div>
              )}
              {(rmse !== null && rmse !== undefined) && (
                <div className="metric-box">
                  <div className="metric-label">RMSE</div>
                  <div className="metric-value">{rmse.toFixed(4)}</div>
                  <div className="metric-description">Root Mean Squared Error (lower is better)</div>
                </div>
              )}
            </div>
          </div>
        )}

        {modelUsed && (
          <div className="info-box">
            <h3>Model Used</h3>
            <p>{modelUsed}</p>
          </div>
        )}


        {/* ROC Curve and Confusion Matrix - Only for Classification */}
        {isClassification && confusionMatrix && (
          <div className="evaluation-metrics-section">
            <h2>Model Evaluation Metrics</h2>
            
            {/* ROC Curve - Only for Binary Classification */}
            {rocCurve && rocCurve.fpr && rocCurve.tpr && Array.isArray(rocCurve.fpr) && Array.isArray(rocCurve.tpr) && rocCurve.fpr.length > 0 && rocCurve.tpr.length > 0 && (
              <div className="plot-container">
                <h3>ROC Curve (AUC = {rocCurve.auc ? rocCurve.auc.toFixed(3) : 'N/A'})</h3>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: '500px' }}>
                  <Plot
                    data={[
                      {
                        x: rocCurve.fpr,
                        y: rocCurve.tpr,
                        type: 'scatter',
                        mode: 'lines',
                        name: `ROC Curve (AUC = ${rocCurve.auc ? rocCurve.auc.toFixed(3) : 'N/A'})`,
                        line: { color: '#1e3c72', width: 2 }
                      },
                      {
                        x: [0, 1],
                        y: [0, 1],
                        type: 'scatter',
                        mode: 'lines',
                        name: 'Random Classifier',
                        line: { color: '#999', dash: 'dash', width: 1 }
                      }
                    ]}
                    layout={{
                      title: 'ROC Curve',
                      xaxis: { title: 'False Positive Rate', range: [0, 1] },
                      yaxis: { title: 'True Positive Rate', range: [0, 1] },
                      autosize: true,
                      showlegend: true,
                      template: 'plotly_white',
                      margin: { l: 60, r: 20, t: 50, b: 60 }
                    }}
                    config={{ responsive: true, displayModeBar: true }}
                    style={{ width: '100%', maxWidth: '800px', height: '500px', minHeight: '500px' }}
                  />
                </div>
              </div>
            )}
            
            {!rocCurve && confusionMatrix.type === "binary" && (
              <div className="info-box">
                <p>ROC curve not available for this model. Some models don't support probability predictions.</p>
              </div>
            )}

            {/* Confusion Matrix */}
            <div className="confusion-matrix-section">
              <h3>Confusion Matrix</h3>
              
              {confusionMatrix.type === "binary" ? (
                <div className="confusion-matrix-binary">
                  <div className="cm-metrics-grid">
                    <div className="cm-metric-box tp">
                      <div className="cm-label">True Positives (TP)</div>
                      <div className="cm-value">{confusionMatrix.tp}</div>
                    </div>
                    <div className="cm-metric-box fp">
                      <div className="cm-label">False Positives (FP)</div>
                      <div className="cm-value">{confusionMatrix.fp}</div>
                    </div>
                    <div className="cm-metric-box fn">
                      <div className="cm-label">False Negatives (FN)</div>
                      <div className="cm-value">{confusionMatrix.fn}</div>
                    </div>
                    <div className="cm-metric-box tn">
                      <div className="cm-label">True Negatives (TN)</div>
                      <div className="cm-value">{confusionMatrix.tn}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="confusion-matrix-multiclass">
                  <div className="cm-metrics-list">
                    {confusionMatrix.classes.map((cls) => {
                      const metrics = confusionMatrix.class_metrics[cls];
                      return (
                        <div key={cls} className="cm-class-metrics">
                          <h4>Class {cls}</h4>
                          <div className="cm-metrics-grid">
                            <div className="cm-metric-box tp">
                              <div className="cm-label">TP</div>
                              <div className="cm-value">{metrics.tp}</div>
                            </div>
                            <div className="cm-metric-box fp">
                              <div className="cm-label">FP</div>
                              <div className="cm-value">{metrics.fp}</div>
                            </div>
                            <div className="cm-metric-box fn">
                              <div className="cm-label">FN</div>
                              <div className="cm-value">{metrics.fn}</div>
                            </div>
                            <div className="cm-metric-box tn">
                              <div className="cm-label">TN</div>
                              <div className="cm-value">{metrics.tn}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Visual Confusion Matrix Heatmap */}
                  {confusionMatrix.matrix && Array.isArray(confusionMatrix.matrix) && confusionMatrix.matrix.length > 0 && (
                    <div className="cm-visual">
                      <Plot
                        data={[
                          {
                            z: confusionMatrix.matrix,
                            type: 'heatmap',
                            colorscale: 'Blues',
                            showscale: true,
                            text: confusionMatrix.matrix.map(row => 
                              row.map(val => String(val))
                            ),
                            texttemplate: '%{text}',
                            textfont: { size: 14, color: '#000' }
                          }
                        ]}
                        layout={{
                          title: 'Confusion Matrix Heatmap',
                          xaxis: { 
                            title: 'Predicted', 
                            side: 'bottom',
                            tickmode: 'array',
                            tickvals: confusionMatrix.classes ? confusionMatrix.classes.map((_, i) => i) : [],
                            ticktext: confusionMatrix.classes ? confusionMatrix.classes.map(c => `Class ${c}`) : []
                          },
                          yaxis: { 
                            title: 'Actual', 
                            autorange: 'reversed',
                            tickmode: 'array',
                            tickvals: confusionMatrix.classes ? confusionMatrix.classes.map((_, i) => i) : [],
                            ticktext: confusionMatrix.classes ? confusionMatrix.classes.map(c => `Class ${c}`) : []
                          },
                          width: 600,
                          height: 500,
                          template: 'plotly_white'
                        }}
                        config={{ responsive: true, displayModeBar: true }}
                        style={{ width: '100%', height: '100%' }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Regression Visualizations */}
        {!isClassification && testAccuracy !== null && testAccuracy !== undefined && predictions.length > 0 && (
          <RegressionVisualizations
            actualValues={actualValues}
            residuals={residuals}
            predictions={predictions}
            parsedRows={parsedRows}
            targetVariable={targetVariable}
          />
        )}

        {parsedRows.length > 0 && predictions.length === parsedRows.length && (
          <div className="predictions-section">
            <div className="predictions-header">
              <h2>Predictions Preview</h2>
              <p className="predictions-info">
                Showing {Math.min(50, parsedRows.length)} of {parsedRows.length} rows. Export to download full dataset with predictions.
              </p>
            </div>
            <div className="predictions-table-wrapper">
              <table className="predictions-table">
                <thead>
                  <tr>
                    {Object.keys(parsedRows[0]).map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                    <th className="prediction-column">Predicted {targetVariable}</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.slice(0, 50).map((row, i) => (
                    <tr key={i}>
                      {Object.keys(parsedRows[0]).map((col, j) => (
                        <td key={j}>{row[col] || ''}</td>
                      ))}
                      <td className="prediction-column">{predictions[i]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="export-section">
              <button
                onClick={() => {
                  const csv = Papa.unparse(
                    parsedRows.map((r, i) => ({
                      ...r,
                      [`Predicted ${targetVariable}`]: predictions[i],
                    }))
                  );
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
                  a.download = `predictions_${modelUsed}_${timestamp}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  setUploadMessage("CSV exported successfully!");
                }}
                className="primary-button export-button"
              >
                Export Full Results as CSV ({parsedRows.length} rows)
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-section">
          {predictions && predictions.length > 0 && (
            <button
              onClick={handleGeneratePredictionReport}
              disabled={predReportLoading}
              className="report-button"
            >
              {predReportLoading ? (
                <>
                  <span className="report-spinner"></span>
                  Generating AI Report…
                </>
              ) : (
                <>
                  📄 Generate AI Report
                </>
              )}
            </button>
          )}
          {predReportError && (
            <p className="report-error">{predReportError}</p>
          )}
          <button onClick={() => navigate("/dashboard")} className="primary-button">
            End Analysis
          </button>
        </div>
      </div>
    </div>
  );
}

